import { useRouter } from "next/router";
import Script from "next/script";

import { useEffect, useMemo } from "react";

import useAuthentication from "hooks/useAuthentication";
import { emberAppBaseUrl } from "utils/envVariables";

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

interface Props {
  type?: "in-article" | "in-feed" | "banner" | "small-responsive" | "small-1";
  slot?: string;
  custom?: {
    layout: string;
    format: string;
    slot: string;
  };
}

interface SnippetProps {
  "data-ad-format": string;
  "data-ad-client": string;
  "data-ad-layout-key"?: string | null;
  "data-ad-layout"?: string | null;
  "data-ad-slot"?: string | null;
  style?: React.CSSProperties;
}

const Snippet = (props: SnippetProps) => {
  useEffect(() => {
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (err) {
      console.error("AdSense error:", err);
    }
  }, []);

  if (!props["data-ad-slot"]) return null;

  return (
    <ins
      className="adsbygoogle"
      {...props}
      data-ad-test={emberAppBaseUrl.includes("staging") ? "on" : "off"}
    ></ins>
  );
};

function AdsContainer({
  type = "in-article",
  slot: customSlot,
  custom,
}: Props) {
  const { authenticated, account } = useAuthentication();
  const router = useRouter();

  useEffect(() => {
    const existingScript = document.getElementById("google_adsense_script");
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.id = "google_adsense_script";
    script.src =
      "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4192570875445332";
    script.type = "text/javascript";
    document.body.appendChild(script);
  }, [router]);

  const adContent = useMemo(() => {
    let format = "fluid";
    let client = "ca-pub-4192570875445332";
    let layout = null;
    let layoutKey = null;
    let slot = null;
    let fullWidthResponsive = null;
    let style: React.CSSProperties = {
      display: "block",
    };

    if (type) {
      switch (type) {
        case "in-article":
          layout = "in-article";
          slot = "2784701508";
          break;
        case "in-feed":
          layoutKey = "-fb+5w+4e-db+86";
          slot = "8144478006";
          break;
        case "small-responsive":
          format = "auto";
          slot = "2621112953";
          fullWidthResponsive = true;
          break;
        case "small-1":
          slot = "6466216553";
          style = {
            ...style,
            width: "300px",
            height: "90px",
          };
          break;
      }
    }

    if (customSlot) {
      slot = customSlot;
    }

    if (custom) {
      layout = custom.layout || layout;
      format = custom.format || format;
      slot = custom.slot || slot;
    }

    return {
      layout,
      format,
      client,
      layoutKey,
      slot,
      fullWidthResponsive,
      style,
    };
  }, [custom, customSlot, type]);

  if (authenticated && !account?.isPreviewPass) {
    return null;
  }

  return (
    <>
      <div className="mt-[20px]">
        <Snippet
          data-ad-layout={adContent.layout}
          data-ad-layout-key={adContent.layoutKey}
          data-ad-format={adContent.format}
          data-ad-client={adContent.client}
          data-ad-slot={adContent.slot}
          data-full-width-responsive={adContent.fullWidthResponsive}
          style={adContent.style}
        />
      </div>
      <Script
        type="text/javascript"
        id="google_adsense_script"
        src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4192570875445332"
      />
    </>
  );
}

export default function GoogleAdsense(props: Props) {
  if (emberAppBaseUrl.includes("staging")) {
    return null;
  }

  return <AdsContainer {...props} />;
}
